import React, { useState, useEffect } from 'react'
import api from '../api'
import Button from '../components/Button'
import { useNavigate } from 'react-router-dom'

interface LetterData {
  letter: string | null
  score: {
    wellbeing_score: number
    burnout_risk: string
    period_start: string
    entries_count: number
  } | null
}

export default function Letter() {
  const [data, setData] = useState<LetterData | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    loadLetter()
  }, [])

  const loadLetter = async () => {
    try {
      const res = await api.get('/scores/letter')
      setData(res.data)
    } catch (err) {
      console.error('Failed to load letter', err)
    } finally {
      setLoading(false)
    }
  }

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return 'var(--green)'
      case 'medium': return 'var(--amber)'
      case 'high': return 'var(--rose)'
      default: return 'var(--muted)'
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}>
        <div style={{
          width: '28px', height: '28px',
          border: '3px solid var(--warm-border)',
          borderTopColor: 'var(--gold)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    )
  }

  if (!data?.letter) {
    return (
      <div className="animate-fadeUp" style={{ textAlign: 'center', padding: '60px 24px' }}>
        <div style={{ fontSize: '64px', marginBottom: '24px' }}>✉</div>
        <h2 style={{
          fontFamily: 'var(--font-display)', fontSize: '28px',
          marginBottom: '12px', color: 'var(--navy)',
        }}>
          Your letter is on its way
        </h2>
        <p style={{
          fontSize: '16px', color: 'var(--muted)',
          maxWidth: '440px', margin: '0 auto 12px', lineHeight: 1.7,
        }}>
          Write a few journal entries this week and your first personal
          letter will appear here — written just for you by Claude AI.
        </p>
        <p style={{
          fontSize: '14px', color: 'var(--muted)',
          maxWidth: '440px', margin: '0 auto 32px',
          fontStyle: 'italic', fontFamily: 'var(--font-display)',
        }}>
          "The more honestly you write, the more meaningful your letter will be."
        </p>
        <Button onClick={() => navigate('/journal')}>
          Write your first entry →
        </Button>
      </div>
    )
  }

  return (
    <div className="animate-fadeUp">
      <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: '4px' }}>
        Your Weekly Letter
      </h2>
      <p style={{ color: 'var(--muted)', marginBottom: '32px' }}>
        Written by Claude based on your entries this week.
      </p>

      {data.score && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '16px', marginBottom: '32px',
        }}>
          {[
            {
              label: 'Wellbeing Score',
              value: Math.round(data.score.wellbeing_score) + '/100',
            },
            {
              label: 'Burnout Risk',
              value: data.score.burnout_risk,
              color: getRiskColor(data.score.burnout_risk),
            },
            {
              label: 'Entries This Week',
              value: data.score.entries_count,
            },
          ].map((item, i) => (
            <div key={i} style={{
              background: 'var(--white)',
              borderRadius: 'var(--radius-md)',
              padding: '16px 20px',
              border: '1px solid var(--cream-dark)',
              textAlign: 'center',
            }}>
              <p style={{
                fontSize: '11px', color: 'var(--muted)',
                textTransform: 'uppercase' as const,
                letterSpacing: '0.08em', marginBottom: '6px',
              }}>
                {item.label}
              </p>
              <p style={{
                fontSize: '20px', fontWeight: 700,
                fontFamily: 'var(--font-display)',
                color: item.color || 'var(--navy)',
                textTransform: 'capitalize' as const,
              }}>
                {item.value}
              </p>
            </div>
          ))}
        </div>
      )}

      <div style={{
        background: 'var(--white)',
        borderRadius: 'var(--radius-lg)',
        padding: '40px',
        border: '1px solid var(--cream-dark)',
        boxShadow: 'var(--shadow-md)',
      }}>
        <div style={{
          borderBottom: '1px solid var(--cream-dark)',
          paddingBottom: '20px', marginBottom: '28px',
          display: 'flex', alignItems: 'center', gap: '12px',
        }}>
          <span style={{ fontSize: '24px' }}>✉</span>
          <div>
            <p style={{
              fontSize: '13px', color: 'var(--muted)',
              marginBottom: '2px',
            }}>
              Your personal letter
            </p>
            <p style={{
              fontSize: '12px', color: 'var(--warm-border)',
            }}>
              Written by Claude AI · Based on your journal entries
            </p>
          </div>
        </div>

        <div style={{
          fontFamily: 'var(--font-display)',
          fontSize: '16px', lineHeight: 1.9,
          color: 'var(--ink-soft)',
          whiteSpace: 'pre-line' as const,
        }}>
          {data.letter}
        </div>

        <div style={{
          marginTop: '32px', paddingTop: '20px',
          borderTop: '1px solid var(--cream-dark)',
          fontSize: '12px', color: 'var(--warm-border)',
          fontStyle: 'italic',
        }}>
          This letter is generated fresh each week based on your journal entries.
          It is private and only visible to you.
        </div>
      </div>
    </div>
  )
}